import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { differenceInBusinessDays, addBusinessDays, addDays } from 'date-fns';
import { Input, Button } from '@/components/ui';
import MarketTypeahead from '@/components/ui/MarketTypeahead';
import MediaFormatTypeahead from '@/components/ui/MediaFormatTypeahead';
// import InventoryRestrictionsDisplay from './InventoryRestrictionsDisplay';
import { useSalesMarkets } from '@/hooks/useSalesMarkets';
import { useMediaFormats } from '@/hooks/useMediaFormats';
import { getBusinessCategorySuggestions, requiresMoviesGap } from '@/lib/business-categories';
import { useShowingTypes } from '@/hooks/useShowingTypes';


// Validation schema for hold request form
const holdRequestSchema = z.object({
  advertiser: z.string().min(1, 'Advertiser name is required'),
  businessCategory: z.string().min(1, 'Business category is required'),
  isCrossRegion: z.boolean().default(false),
  // Line item fields
  market: z.string().min(1, 'Market is required'),
  mediaType: z.string().min(1, 'Media type is required').refine(
    (val) => { const n = parseInt(val, 10); return !isNaN(n) && n > 0; },
    { message: 'Select a valid media format' }
  ),
  unitProductId: z.string().min(1, 'Unit/Product ID is required'),
  holdStartDate: z.string().min(1, 'Hold start date is required'),
  flightStartDate: z.string().min(1, 'Flight start date is required'),
  flightEndDate: z.string().min(1, 'Flight end date is required'),
  isExceptionHold: z.boolean().default(false),
  showingType: z.string().optional(),
}).refine(
  (data) => {
    // Date range validation: flight start <= flight end
    const flightStart = new Date(data.flightStartDate);
    const flightEnd = new Date(data.flightEndDate);
    return flightStart <= flightEnd;
  },
  {
    message: 'Flight end date must be after flight start date',
    path: ['flightEndDate'],
  }
);

type HoldRequestFormData = z.infer<typeof holdRequestSchema>;

interface HoldRequestFormProps {
  onSubmit: (data: HoldRequestFormData) => void | Promise<void>;
  isLoading?: boolean;
  defaultValues?: Partial<HoldRequestFormData>;
}

export default function HoldRequestForm({
  onSubmit,
  isLoading = false,
  defaultValues,
}: HoldRequestFormProps) {
  const [businessCategoryInput, setBusinessCategoryInput] = useState('');
  const [showBusinessCategorySuggestions, setShowBusinessCategorySuggestions] = useState(false);
  
  // Fetch sales markets from API
  const { data: salesMarkets, isLoading: marketsLoading } = useSalesMarkets();

  // Fetch media formats from API
  const { data: mediaFormats, isLoading: mediaFormatsLoading } = useMediaFormats();
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<HoldRequestFormData>({
    resolver: zodResolver(holdRequestSchema),
    defaultValues: {
      isCrossRegion: false,
      isExceptionHold: false,
      ...defaultValues,
    },
  });

  const isCrossRegion = watch('isCrossRegion');
  const businessCategory = watch('businessCategory');
  const mediaType = watch('mediaType');
  const flightStartDate = watch('flightStartDate');

  // Fetch showing types from API
  const { data: showingTypes = [], isLoading: isLoadingShowingTypes } = useShowingTypes();

  // Resolve media format name for expiration calculation
  const mediaFormatName = useMemo(() => {
    if (!mediaType || !mediaFormats) return '';
    const id = parseInt(mediaType, 10);
    if (isNaN(id)) return '';
    const format = mediaFormats.find(f => f.id === id);
    return format?.name?.toLowerCase() || '';
  }, [mediaType, mediaFormats]);

  // Compute hold expiration date client-side for display
  const computedExpirationDate = useMemo(() => {
    const today = new Date();
    if (businessCategory && requiresMoviesGap(businessCategory)) {
      return addDays(today, 60);
    }
    // OPE-465: whole-word match, mirroring the backend predicate (isDigitalMediaType,
    // /\bdigital\b/i) so this UX preview never disagrees with the server's policy.
    if (/\bdigital\b/i.test(mediaFormatName) && flightStartDate) {
      const flight = new Date(flightStartDate);
      const gap = differenceInBusinessDays(flight, today);
      if (gap <= 14) {
        return addBusinessDays(today, 3);
      }
    }
    return addDays(today, 5);
  }, [businessCategory, mediaFormatName, flightStartDate]);

  const formattedExpiration = useMemo(() => {
    if (!computedExpirationDate) return '';
    return computedExpirationDate.toISOString().split('T')[0];
  }, [computedExpirationDate]);

  // Get business category suggestions
  const businessCategorySuggestions = useMemo(() => {
    return getBusinessCategorySuggestions(businessCategoryInput);
  }, [businessCategoryInput]);

  // Show Movies gap warning
  useEffect(() => {
    if (requiresMoviesGap(businessCategory) && formattedExpiration && flightStartDate) {
      const holdExpDate = new Date(formattedExpiration);
      const flightStart = new Date(flightStartDate);
      const gap = differenceInBusinessDays(flightStart, holdExpDate);
      
      if (gap < 60 && gap >= 0) {
        console.warn(`Movies gap warning: ${gap} business days (minimum 60 required)`);
      }
    }
  }, [businessCategory, formattedExpiration, flightStartDate]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Hold Request Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-neutral-900">Hold Request Information</h3>
        
        <Input
          label="Advertiser Name"
          placeholder="Enter advertiser name"
          error={errors.advertiser?.message}
          {...register('advertiser')}
        />

        <div className="relative">
          <label htmlFor="businessCategory" className="block text-sm font-medium text-neutral-600 mb-1">
            Business Category <span className="text-red-500">*</span>
          </label>
          <input
            id="businessCategory"
            type="text"
            placeholder="Type to search categories..."
            className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
            value={businessCategoryInput}
            onChange={(e) => {
              setBusinessCategoryInput(e.target.value);
              setValue('businessCategory', e.target.value);
              setShowBusinessCategorySuggestions(true);
            }}
            onFocus={() => setShowBusinessCategorySuggestions(true)}
            onBlur={() => setTimeout(() => setShowBusinessCategorySuggestions(false), 200)}
          />
          {errors.businessCategory && (
            <p className="mt-1 text-sm text-red-600">{errors.businessCategory.message}</p>
          )}
          
          {showBusinessCategorySuggestions && businessCategorySuggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-md  max-h-60 overflow-auto">
              {businessCategorySuggestions.map((category) => (
                <button
                  key={category}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-neutral-100 text-sm"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setBusinessCategoryInput(category);
                    setValue('businessCategory', category);
                    setShowBusinessCategorySuggestions(false);
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          )}
        </div>

        {requiresMoviesGap(businessCategory) && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Movies Requirements:</strong> A minimum 60 business-day gap is required 
              between the hold expiration date and flight start date.
            </p>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isCrossRegion"
            className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
            {...register('isCrossRegion')}
          />
          <label htmlFor="isCrossRegion" className="text-sm font-medium text-neutral-600">
            This is a cross-region request
          </label>
        </div>

        {isCrossRegion && (
          <div className="p-4 bg-outfront-purple-light border border-outfront-purple rounded-md">
            <p className="text-sm text-outfront-purple">
              <strong>Cross-Region Request:</strong> Service AE can be assigned to individual line items
              after the hold request is created.
            </p>
          </div>
        )}
      </div>

      {/* Inventory Line Item Information */}
      <div className="space-y-4 pt-6 border-t border-neutral-200">
        <h3 className="text-lg font-semibold text-neutral-900">Initial Line Item</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MarketTypeahead
            value={watch('market')}
            onChange={(code) => setValue('market', code, { shouldValidate: true })}
            markets={salesMarkets ?? []}
            isLoading={marketsLoading}
            error={errors.market?.message}
            label="Market"
          />

          <MediaFormatTypeahead
            value={watch('mediaType') || ''}
            onChange={(id) => setValue('mediaType', id, { shouldValidate: true })}
            mediaFormats={mediaFormats ?? []}
            isLoading={mediaFormatsLoading}
            error={errors.mediaType?.message}
            label="Media Type"
          />
        </div>

        {/* Display inventory restrictions when market and media type are selected */}
        {/* TODO: Rebuild inventory restrictions feature */}
        {/* {market && mediaType && (
          <InventoryRestrictionsDisplay market={market} mediaType={mediaType} />
        )} */}

        <Input
          label="Unit/Product ID"
          placeholder="Enter unit or product ID"
          error={errors.unitProductId?.message}
          {...register('unitProductId')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Hold Start Date"
            type="date"
            error={errors.holdStartDate?.message}
            {...register('holdStartDate')}
          />

          <Input
            label="Hold Expiration Date (auto-calculated)"
            type="date"
            value={formattedExpiration}
            disabled
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Flight Start Date"
            type="date"
            error={errors.flightStartDate?.message}
            {...register('flightStartDate')}
          />

          <Input
            label="Flight End Date"
            type="date"
            error={errors.flightEndDate?.message}
            {...register('flightEndDate')}
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isExceptionHold"
            className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
            {...register('isExceptionHold')}
          />
          <label htmlFor="isExceptionHold" className="text-sm font-medium text-neutral-600">
            This is an exception hold (requires special approval)
          </label>
        </div>

        <div>
          <label htmlFor="showingType" className="block text-sm font-medium text-neutral-600 mb-1">
            Showing Type
          </label>
          <select
            id="showingType"
            className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={isLoadingShowingTypes || showingTypes.length === 0}
            {...register('showingType')}
          >
            <option value="">{isLoadingShowingTypes ? 'Loading...' : showingTypes.length === 0 ? 'None available' : 'Select...'}</option>
            {showingTypes.map((opt) => (
              <option key={opt.code} value={opt.code}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-neutral-200">
        <Button type="button" variant="outline" disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isLoading={isLoading}>
          Create Hold Request
        </Button>
      </div>
    </form>
  );
}

